// Till payments (Phase 11, ADR 0037): the company's own M-Pesa till. A
// company_admin enters the till and its Daraja consumer key, consumer secret
// and passkey; they are encrypted and never shown again, and saving sends
// the till to a platform administrator for approval. Every member sees the
// till's status and the recent till payments.
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  PasswordInput,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { TILL_STATUS_COLOUR } from "../../shared/ui/tillPayments";
import { companyKey, ConfirmDialog } from "./common";

type Till = Schemas["Till"];
type Kind = Till["kind"];
type Environment = Till["environment"];

const STATUS_COLOUR: Record<Till["status"], string> = { pending: "yellow", approved: "green", suspended: "red" };
const DIGITS = /^[0-9]{5,10}$/;

/** useTill answers null when the company has no till yet (the API's 404). */
function useTill(companyId: string) {
  return useQuery({
    queryKey: companyKey(companyId, "till"),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/companies/{company_id}/till", {
        params: { path: { company_id: companyId } },
      });
      if (response.status === 404) return null;
      if (!data) throw error;
      return data;
    },
  });
}

export function TillTab({ companyId, admin }: { companyId: string; admin: boolean }) {
  const { t } = useTranslation();
  const till = useTill(companyId);
  const [editing, setEditing] = useState(false);

  if (till.isPending) return <Loader size="sm" aria-label={t("common.loading")} />;
  if (till.isError) return <FormError message={errorMessage(till.error, t("error.generic"))} />;
  const current = till.data;
  return (
    <Stack gap="lg">
      <Paper withBorder p="md" radius="md" data-testid="till-section">
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={2} size="h4">
              {t("till.title")}
            </Title>
            {current && (
              <Badge color={STATUS_COLOUR[current.status]} data-testid="till-status">
                {t(`till.status_${current.status}`)}
              </Badge>
            )}
          </Group>
          <Text size="sm">{t("till.intro")}</Text>
          {current && (
            <TillSummary till={current} companyId={companyId} admin={admin} onChange={() => setEditing(true)} />
          )}
          {admin ? (
            (!current || editing) && <TillForm companyId={companyId} till={current} onDone={() => setEditing(false)} />
          ) : (
            <Text size="sm" c="dimmed">
              {t("till.viewer")}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            {t("till.api_hint")}
          </Text>
        </Stack>
      </Paper>
      <TillPayments companyId={companyId} />
    </Stack>
  );
}

function TillSummary({
  till,
  companyId,
  admin,
  onChange,
}: {
  till: Till;
  companyId: string;
  admin: boolean;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const path = { params: { path: { company_id: companyId } } };

  const test = async () => {
    setError(null);
    setNotice(null);
    setBusy("test");
    const { response, error } = await api.POST("/companies/{company_id}/till/test", path);
    setBusy(null);
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("till.test_ok"));
  };
  const remove = async () => {
    setBusy("remove");
    const { response, error } = await api.DELETE("/companies/{company_id}/till", path);
    setBusy(null);
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    setRemoving(false);
    queryClient.setQueryData(companyKey(companyId, "till"), null);
  };

  return (
    <Stack gap="xs">
      <Text size="sm" data-testid="till-summary">
        {t("till.configured", {
          kind: t(`till.kind_${till.kind}`),
          till: till.till_number,
          store: till.short_code,
          environment: t(`till.env_${till.environment}`),
          hint: till.key_hint,
        })}
      </Text>
      {till.status === "suspended" && till.reason && (
        <Alert color="red" role="alert">
          {t("till.suspended_reason", { reason: till.reason })}
        </Alert>
      )}
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {admin && (
        <Group gap="sm">
          <Button size="xs" variant="light" loading={busy === "test"} onClick={() => void test()}>
            {t("till.test")}
          </Button>
          <Button size="xs" variant="default" onClick={onChange}>
            {t("till.change")}
          </Button>
          <Button size="xs" variant="subtle" color="red" onClick={() => setRemoving(true)}>
            {t("till.remove")}
          </Button>
        </Group>
      )}
      <ConfirmDialog
        opened={removing}
        title={t("till.remove_title")}
        confirmLabel={t("till.remove")}
        busy={busy === "remove"}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(false)}
      >
        <Text size="sm">{t("till.remove_body")}</Text>
      </ConfirmDialog>
    </Stack>
  );
}

function TillForm({ companyId, till, onDone }: { companyId: string; till: Till | null; onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind>(till?.kind ?? "till");
  const [shortCode, setShortCode] = useState(till?.short_code ?? "");
  const [tillNumber, setTillNumber] = useState(till?.till_number ?? "");
  const [environment, setEnvironment] = useState<Environment>(till?.environment ?? "sandbox");
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [passkey, setPasskey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const store = shortCode.trim();
    const number = kind === "paybill" ? store : tillNumber.trim();
    if (!DIGITS.test(store) || !DIGITS.test(number)) return setError(t("till.invalid_code"));
    if ([key, secret, passkey].some((v) => v.trim().length < 8)) return setError(t("till.invalid_credentials"));
    setBusy(true);
    const { data, error } = await api.PUT("/companies/{company_id}/till", {
      params: { path: { company_id: companyId } },
      body: {
        kind,
        short_code: store,
        till_number: number,
        environment,
        consumer_key: key.trim(),
        consumer_secret: secret.trim(),
        passkey: passkey.trim(),
      },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setKey("");
    setSecret("");
    setPasskey("");
    setNotice(t("till.saved"));
    queryClient.setQueryData(companyKey(companyId, "till"), data);
    onDone();
  };

  return (
    <form onSubmit={(e) => void save(e)} data-testid="till-form">
      <Stack gap="sm" maw={520}>
        {notice && <Notice>{notice}</Notice>}
        <Stack gap={4}>
          <Text size="sm" fw={500} id="till-kind">
            {t("till.kind")}
          </Text>
          <SegmentedControl
            aria-labelledby="till-kind"
            value={kind}
            onChange={(v) => setKind(v as Kind)}
            data={[
              { value: "till", label: t("till.kind_till") },
              { value: "paybill", label: t("till.kind_paybill") },
            ]}
            w="fit-content"
          />
        </Stack>
        <TextInput
          label={kind === "paybill" ? t("till.short_code_paybill") : t("till.short_code")}
          description={kind === "paybill" ? undefined : t("till.short_code_hint")}
          value={shortCode}
          onChange={(e) => setShortCode(e.currentTarget.value)}
          inputMode="numeric"
          maxLength={10}
          required
        />
        {kind === "till" && (
          <TextInput
            label={t("till.till_number")}
            description={t("till.till_number_hint")}
            value={tillNumber}
            onChange={(e) => setTillNumber(e.currentTarget.value)}
            inputMode="numeric"
            maxLength={10}
            required
          />
        )}
        <Select
          label={t("till.environment")}
          value={environment}
          onChange={(v) => v && setEnvironment(v as Environment)}
          data={[
            { value: "sandbox", label: t("till.env_sandbox") },
            { value: "production", label: t("till.env_production") },
          ]}
          allowDeselect={false}
        />
        <Text size="xs" c="dimmed">
          {t("till.credentials_hint")}
        </Text>
        <TextInput
          label={t("till.consumer_key")}
          value={key}
          onChange={(e) => setKey(e.currentTarget.value)}
          autoComplete="off"
          maxLength={200}
          required
        />
        <PasswordInput
          label={t("till.consumer_secret")}
          value={secret}
          onChange={(e) => setSecret(e.currentTarget.value)}
          autoComplete="new-password"
          maxLength={200}
          required
        />
        <PasswordInput
          label={t("till.passkey")}
          description={t("till.passkey_hint")}
          value={passkey}
          onChange={(e) => setPasskey(e.currentTarget.value)}
          autoComplete="new-password"
          maxLength={200}
          required
        />
        <FormError message={error} />
        <Group>
          <Button type="submit" loading={busy}>
            {t("till.save")}
          </Button>
          {till && (
            <Button variant="subtle" onClick={onDone}>
              {t("mpesa.cancel")}
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
}

function TillPayments({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const payments = useQuery({
    queryKey: companyKey(companyId, "till-payments"),
    queryFn: async () => {
      const { data, error } = await api.GET("/companies/{company_id}/till-payments", {
        params: { path: { company_id: companyId }, query: { limit: 50 } },
      });
      if (!data) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
  const items = payments.data?.items ?? [];
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Title order={2} size="h4">
          {t("till.payments_title")}
        </Title>
        {items.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="till-payments-empty">
            {t("till.payments_empty")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table aria-label={t("till.payments_title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("mpesa.when")}</Table.Th>
                  <Table.Th>{t("till.reference")}</Table.Th>
                  <Table.Th>{t("mpesa.amount")}</Table.Th>
                  <Table.Th>{t("mpesa.status")}</Table.Th>
                  <Table.Th>{t("mpesa.receipt")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((p) => (
                  <Table.Tr key={p.payment_id} data-testid="till-payment-row">
                    <Table.Td>{f.dateTime(p.created_at)}</Table.Td>
                    <Table.Td>{p.account_reference}</Table.Td>
                    <Table.Td>{f.money(p.amount * 100, p.currency)}</Table.Td>
                    <Table.Td>
                      <Badge color={TILL_STATUS_COLOUR[p.status]}>{t(`till_status.${p.status}`)}</Badge>
                    </Table.Td>
                    <Table.Td>{p.receipt ?? ""}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}
